import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Select, Tag, Button, Space, Spin, Alert } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconId } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminAgeVerificationUsers,
  overrideAdminAgeVerification,
  IAdminAgeVerificationUser,
} from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, dateSorter } from '../../../helpers/adminTableHelpers';

const { Title } = Typography;
const { Search } = Input;

// Per-user age-verification status (HeavyDutyMachineryUser.AgeVerified/AgeVerifiedAt),
// filterable by status and searchable by name/email, with a manual
// override -- grant (for a user who verified another way, outside the
// self-service QR-scan flow -- see AgeVerificationController) or revoke.
// Both directions require a reason, same as suspend.
const AgeVerificationSection: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<IAdminAgeVerificationUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [verified, setVerified] = useState<boolean | undefined>(undefined);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [overrideTarget, setOverrideTarget] = useState<{ userId: string; verified: boolean } | undefined>(undefined);

  const loadUsers = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminAgeVerificationUsers(verified, search)
      .then(setUsers)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [verified, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleOverride = async (reason: string) => {
    if (!overrideTarget) return;
    try {
      await overrideAdminAgeVerification(overrideTarget.userId, overrideTarget.verified, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setOverrideTarget(undefined);
      loadUsers();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_AGE_VERIFICATION)}
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          onSearch={(value) => setSearch(value || undefined)}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_STATUS_FILTER_ALL)}
          style={{ maxWidth: 180, width: '100%' }}
          value={verified === undefined ? undefined : String(verified)}
          onChange={(value) => setVerified(value === undefined ? undefined : value === 'true')}
          options={[
            { value: 'true', label: translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_STATUS_VERIFIED) },
            { value: 'false', label: translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_STATUS_NOT_VERIFIED) },
          ]}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_LOAD_ERROR)} />
      ) : users.length === 0 ? (
        <EmptyState
          icon={<IconId size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminAgeVerificationUser>
          rowKey="userId"
          dataSource={users}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.userId,
            title: record.displayName,
            subtitle: record.email,
            badge: (
              <Tag color={record.ageVerified ? 'green' : 'default'} style={{ marginInlineEnd: 0 }}>
                {translate(
                  t,
                  record.ageVerified
                    ? TRANSLATIONS.ADMIN_AGE_VERIFICATION_STATUS_VERIFIED
                    : TRANSLATIONS.ADMIN_AGE_VERIFICATION_STATUS_NOT_VERIFIED,
                )}
              </Tag>
            ),
            fields: [
              {
                label: translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_COL_VERIFIED_AT),
                value: record.ageVerifiedAt ? new Date(record.ageVerifiedAt).toLocaleDateString() : '—',
              },
            ],
            actions: record.ageVerified ? (
              <Button size="small" danger onClick={() => setOverrideTarget({ userId: record.userId, verified: false })}>
                {translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_REVOKE_BUTTON)}
              </Button>
            ) : (
              <Button size="small" onClick={() => setOverrideTarget({ userId: record.userId, verified: true })}>
                {translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_GRANT_BUTTON)}
              </Button>
            ),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_COL_NAME),
              dataIndex: 'displayName',
              key: 'displayName',
              sorter: textSorter<IAdminAgeVerificationUser>('displayName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_COL_EMAIL),
              dataIndex: 'email',
              key: 'email',
              sorter: textSorter<IAdminAgeVerificationUser>('email'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_COL_STATUS),
              dataIndex: 'ageVerified',
              key: 'ageVerified',
              render: (value: boolean) => (
                <Tag color={value ? 'green' : 'default'}>
                  {translate(t, value ? TRANSLATIONS.ADMIN_AGE_VERIFICATION_STATUS_VERIFIED : TRANSLATIONS.ADMIN_AGE_VERIFICATION_STATUS_NOT_VERIFIED)}
                </Tag>
              ),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_COL_VERIFIED_AT),
              dataIndex: 'ageVerifiedAt',
              key: 'ageVerifiedAt',
              sorter: dateSorter<IAdminAgeVerificationUser>('ageVerifiedAt'),
              render: (value?: string) => (value ? new Date(value).toLocaleDateString() : '—'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_COL_ACTIONS),
              key: 'actions',
              render: (_: unknown, record: IAdminAgeVerificationUser) =>
                record.ageVerified ? (
                  <Button size="small" danger onClick={() => setOverrideTarget({ userId: record.userId, verified: false })}>
                    {translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_REVOKE_BUTTON)}
                  </Button>
                ) : (
                  <Button size="small" onClick={() => setOverrideTarget({ userId: record.userId, verified: true })}>
                    {translate(t, TRANSLATIONS.ADMIN_AGE_VERIFICATION_GRANT_BUTTON)}
                  </Button>
                ),
            },
          ]}
        />
      )}

      <AdminReasonModal
        open={overrideTarget !== undefined}
        title={translate(
          t,
          overrideTarget?.verified
            ? TRANSLATIONS.ADMIN_AGE_VERIFICATION_GRANT_MODAL_TITLE
            : TRANSLATIONS.ADMIN_AGE_VERIFICATION_REVOKE_MODAL_TITLE
        )}
        confirmLabel={translate(
          t,
          overrideTarget?.verified ? TRANSLATIONS.ADMIN_AGE_VERIFICATION_GRANT_BUTTON : TRANSLATIONS.ADMIN_AGE_VERIFICATION_REVOKE_BUTTON
        )}
        onCancel={() => setOverrideTarget(undefined)}
        onConfirm={handleOverride}
      />
    </div>
  );
};

export default AgeVerificationSection;
